<template>
  <div class="mt-20" :class="{ 'vz-direction-calendar-terminal-time-disabled': disabled }">
    <div class="text-subtitle color-dark">
      Время
    </div>
    <div class="flex flex-align-items-center mt-5">
      <span class="text-body">
        {{ timeLabel }}
      </span>
      <div>
        <vz-select
          :modelValue="calendarTerminalTime"
          :disabled="disabled"
          :items="times"
          big
          class="small-width mb-0 ml-10"
          @change="onTimeChange"
        />
      </div>
    </div>
    <div v-if="date && date.note" class="text-caption color-primary mt-10">
      {{ date.note }}
    </div>
  </div>
</template>

<script setup lang="ts">
// vz-direction-calendar-terminal-time & vz-direction-calendar-pickup-point-time
import { useDayjs } from '#dayjs'
import { TimetableDate, TimetableTime } from '~/types/types'
import { useOrderStore } from '~/stores/order'
import { storeToRefs } from 'pinia'
import {PropType} from 'vue';
import {objTime} from "~/common/functions";

const emit = defineEmits(['select-time'])

const props = defineProps({
  date: {
    type: Object as PropType<TimetableDate>,
    default: () => null
  },
  disabled:{
    type: Boolean,
    default: false
  },
  directionTo: {
    type: Boolean,
    default: false
  }
})

const dayjs = useDayjs()
const { orderForm, timetable, orderDates, orderType } = storeToRefs(useOrderStore())
const orderStore = useOrderStore()
const route = useRoute()

const directionName = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const hasGuid = computed((): boolean => {
  return !!route.params.guid
})

const timeLabel = computed((): string => {
  return !props.directionTo ? 'до' : 'с'
})

const calendarTerminalTime = computed((): string => {
  return orderForm.value.directions[directionName.value].calendar.terminalTime
})

const timetableTimes = computed((): TimetableTime[] => {
  if (!props.date) {
    return []
  }

  const time = dayjs(props.date.defaultTime).format('HH:mm')
  const orderTerminalTime = orderDates.value[directionName.value]?.terminalTime
  const result: TimetableTime[] = []

  if (props.directionTo) {
    result.push({
      value: time,
      title: time,
      type: 'terminal',
      minutes: dayjs.duration(objTime(time)).asMinutes(),
      url: ''
    })
  } else {
    timetable.value?.destination.forEach((g) => {
      result.push({
        value: g.groupTime,
        title: g.groupTime,
        type: 'terminal',
        minutes: g.groupTimeMin,
        url: ''
      })
    })
    if (orderType.value === 'multi') {
      const additional = orderStore.multiOrder.timetableSplitList
        .filter(minutes => !result.find(item => item.minutes === minutes))

      additional.forEach(minutes => {
        const str = dayjs.utc().startOf('day').add(minutes, 'minutes').format('HH:mm')
        result.push({
          value: str,
          title: str,
          type: 'terminal',
          minutes: minutes,
          url: ''
        })
      })
    }
  }

  if (orderTerminalTime && !result.find(item => item.value === orderTerminalTime)) {
    // TODO: нужен тест
    result.unshift({
      value: orderTerminalTime,
      title: orderTerminalTime,
      type: 'terminal',
      minutes: dayjs.duration(orderTerminalTime).asMinutes(),
      url: ''
    })
  }

  return result
})

const times = computed((): TimetableTime[] => {
  // additional? и надпись можно раньше?
  return timetableTimes.value
})

// При смене дня отправки/прибытия выставляем возможное время
watch(() => props.date, () => {
  if (orderStore.skipDefaultTimeFlag) {
    return
  }

  let terminalTime = calendarTerminalTime.value// hasGuid.value ? calendarTerminalTime.value : ''
  if (!times.value.find(t => t.value === terminalTime)) {
    terminalTime = ''
  }

  orderForm.value.directions[directionName.value].calendar.terminalTime = terminalTime || times.value?.[0]?.value
}, { immediate: true, deep: true })

async function onTimeChange(time: TimetableTime): Promise<void> {
  if (time.value !== orderForm.value.directions[directionName.value].calendar.terminalTime) {
    orderForm.value.directions[directionName.value].calendar.terminalTime = time.value
    await nextTick()
    emit('select-time', time)
  }
}

</script>

<style lang="scss" scoped>
.vz-direction-calendar-terminal-time-disabled {
    color: $inactive  !important;

    .text-subtitle,
    .text-body {
        color: $inactive  !important;
    }
}
</style>
